import { useState } from 'react';
import { Link } from 'react-router-dom';

import ImageLightbox from '@/components/ui/ImageLightbox';
import { ROUTES } from '@/config/routes';
import type { Competition } from '@/lib/api/competitionsApi';
import type { Team } from '@/lib/api/teamsApi';
import { getFileUrl } from '@/lib/file-url';
import {
  COMPOSITION_TYPE_LABELS,
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS_UPPER as TYPE_LABELS,
  formatDateFullFr,
} from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import {
  ArrowRight,
  BadgeCheck,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Euro,
  MapPin,
  Share2,
  Shield,
  Trophy,
  Users,
} from 'lucide-react';

type HeroTeam = Pick<Team, 'id' | 'name'>;

type Props = {
  competition: Competition;
  myTeam: HeroTeam | null;
  isFull: boolean;
  isOpen: boolean;
  hasResults: boolean;
  resultsUrl: string;
  registrationExpired?: boolean;
  registrationNotYetOpen?: boolean;
  registrationOpensAt?: string | null;
  registrationDisabledByPlatform?: boolean;
  isPast?: boolean;
};

export default function ConcourHero({
  competition,
  myTeam,
  isFull,
  isOpen,
  hasResults,
  resultsUrl,
  registrationExpired = false,
  registrationNotYetOpen = false,
  registrationOpensAt = null,
  registrationDisabledByPlatform = false,
  isPast = false,
}: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const posterUrl = competition.files?.[0] ? getFileUrl(competition.files[0]) : null;
  return (
    <section className='pt-32 pb-16 px-6 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white relative overflow-hidden'>
      <div className='absolute inset-0 opacity-10'>
        <div className='absolute top-10 right-20 w-64 h-64 rounded-full bg-white/20' />
        <div className='absolute bottom-10 left-10 w-48 h-48 rounded-full bg-white/10' />
      </div>

      <div className='max-w-7xl mx-auto relative z-10'>
        {/* Breadcrumb */}
        <div className='flex items-center gap-2 text-blue-200 text-sm mb-8'>
          <Link to={ROUTES.HOME} className='hover:text-white transition-colors'>
            Accueil
          </Link>
          <ChevronRight className='w-4 h-4' />
          <Link to={ROUTES.SEARCH_CONCOURS} className='hover:text-white transition-colors'>
            Concours
          </Link>
          <ChevronRight className='w-4 h-4' />
          <span className='text-white truncate max-w-48'>{competition.name}</span>
        </div>

        <div className='flex flex-col lg:flex-row gap-10'>
          {/* Affiche du concours */}
          <div className='lg:w-1/3 shrink-0'>
            {posterUrl ? (
              <>
                {/* Feature 4 (2026-08) — affiche cliquable → lightbox plein écran */}
                <button
                  type='button'
                  onClick={() => setLightboxOpen(true)}
                  className='group block w-full cursor-zoom-in'
                  aria-label="Agrandir l'affiche du concours"
                >
                  <img
                    src={posterUrl}
                    alt={`Affiche — ${competition.name}`}
                    className='w-full aspect-[3/4] object-cover rounded-3xl shadow-2xl transition group-hover:brightness-105'
                  />
                </button>
                {lightboxOpen && (
                  <ImageLightbox
                    src={posterUrl}
                    alt={`Affiche — ${competition.name}`}
                    onClose={() => setLightboxOpen(false)}
                  />
                )}
              </>
            ) : (
              <div className='aspect-[3/4] bg-gradient-to-br from-blue-400/30 to-indigo-500/30 rounded-3xl border border-white/20 flex items-center justify-center'>
                <div className='text-center'>
                  <Trophy className='w-16 h-16 text-white/40 mx-auto mb-4' />
                  <span className='text-white/40 text-sm'>Affiche du concours</span>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className='flex-1'>
            {/* Status badges */}
            <div className='flex flex-wrap items-center gap-2 mb-4'>
              {competition.status === CompetitionStatus.REGISTRATION_OPEN &&
                !isFull &&
                (registrationNotYetOpen ? (
                  <span className='inline-flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                    <Clock className='w-3.5 h-3.5' /> Ouvre le{' '}
                    {registrationOpensAt ? formatDateFullFr(registrationOpensAt) : ''}
                  </span>
                ) : registrationExpired ? (
                  <span className='inline-flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                    <Clock className='w-3.5 h-3.5' /> Inscriptions fermées
                  </span>
                ) : registrationDisabledByPlatform ? (
                  <span className='inline-flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                    <Clock className='w-3.5 h-3.5' /> Inscriptions suspendues
                  </span>
                ) : (
                  <span className='inline-flex items-center gap-1.5 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                    <BadgeCheck className='w-3.5 h-3.5' /> Inscriptions ouvertes
                  </span>
                ))}
              {isFull && (
                <span className='inline-flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                  Complet
                </span>
              )}
              {competition.status === CompetitionStatus.IN_PROGRESS && (
                <span className='inline-flex items-center gap-1.5 bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                  <Clock className='w-3.5 h-3.5' /> En cours
                </span>
              )}
              {competition.status === CompetitionStatus.FINISHED && (
                <span className='inline-flex items-center gap-1.5 bg-slate-500 text-white px-3 py-1.5 rounded-full text-xs font-bold'>
                  Terminé
                </span>
              )}
              <span className='px-3 py-1.5 bg-white/10 text-white text-xs font-bold rounded-full'>
                {competition.type ? TYPE_LABELS[competition.type] : '—'}
              </span>
              <span className='px-3 py-1.5 bg-white/10 text-white text-xs font-bold rounded-full'>
                {COMPOSITION_TYPE_LABELS[competition.compositionType]}
              </span>
              <span className='px-3 py-1.5 bg-white/10 text-white text-xs font-bold rounded-full'>
                {competition.level ? LEVEL_LABELS[competition.level] : '—'}
              </span>
              {competition.restrictedToMembers && (
                <span className='inline-flex items-center gap-1.5 bg-purple-500/30 text-purple-100 px-3 py-1.5 rounded-full text-xs font-bold'>
                  <Shield className='w-3.5 h-3.5' /> Licence requise
                </span>
              )}
              {competition.restrictedAffiliates && (
                <span className='inline-flex items-center gap-1.5 bg-indigo-500/30 text-indigo-100 px-3 py-1.5 rounded-full text-xs font-bold'>
                  <Users className='w-3.5 h-3.5' /> Clubs affiliés
                </span>
              )}
            </div>

            <h1 className='text-4xl md:text-5xl font-extrabold mb-8 leading-tight'>{competition.name}</h1>

            {/* Info Grid */}
            <div className='grid grid-cols-2 gap-4 mb-8'>
              <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-4'>
                <div className='flex items-center gap-2 text-blue-200 text-sm mb-1'>
                  <Calendar className='w-4 h-4' /> Date
                </div>
                <div className='font-bold text-lg'>{competition.date ? formatDateFullFr(competition.date) : '—'}</div>
              </div>
              <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-4'>
                <div className='flex items-center gap-2 text-blue-200 text-sm mb-1'>
                  <Users className='w-4 h-4' /> Équipes
                </div>
                <div className='font-bold text-lg'>
                  {competition.currentTeams}/{competition.maxTeams ?? '?'} {isPast ? 'participants' : 'inscrits'}
                </div>
              </div>
              {(competition.venue ?? competition.city) && (
                <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-4'>
                  <div className='flex items-center gap-2 text-blue-200 text-sm mb-1'>
                    <MapPin className='w-4 h-4' /> Lieu
                  </div>
                  <div className='font-bold text-lg'>{competition.venue ?? competition.city}</div>
                </div>
              )}
              <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-4'>
                <div className='flex items-center gap-2 text-blue-200 text-sm mb-1'>
                  <Euro className='w-4 h-4' /> Prix d&apos;inscription
                </div>
                <div className='font-bold text-lg'>
                  {competition.price ? `${(competition.price / 100).toFixed(0)}€ / équipe` : 'Gratuit'}
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className='flex flex-wrap gap-4'>
              {myTeam ? (
                <div className='flex items-center gap-3 bg-emerald-500/20 border border-emerald-400/40 text-white px-6 py-4 rounded-2xl font-bold text-lg'>
                  <CheckCircle2 className='w-6 h-6 text-emerald-300 shrink-0' />
                  <div>
                    <div className='text-emerald-200 text-xs font-semibold uppercase tracking-wide mb-0.5'>
                      {isPast ? 'Vous avez participé' : 'Vous êtes inscrit'}
                    </div>
                    <div className='text-white font-bold'>{myTeam.name}</div>
                  </div>
                </div>
              ) : registrationDisabledByPlatform ? (
                <div className='bg-white/20 text-white/70 px-8 py-4 rounded-2xl font-bold text-lg cursor-not-allowed flex items-center gap-2'>
                  Inscriptions fermées temporairement
                </div>
              ) : registrationNotYetOpen ? (
                <div className='bg-amber-500/20 border border-amber-400/40 text-amber-100 px-8 py-4 rounded-2xl font-bold text-lg cursor-not-allowed flex items-center gap-2'>
                  <Clock className='w-5 h-5 shrink-0' />
                  Inscriptions ouvertes le {registrationOpensAt ? formatDateFullFr(registrationOpensAt) : 'bientôt'}
                </div>
              ) : isOpen ? (
                <Link
                  to={ROUTES.REGISTER_CONCOUR.replace(':id', String(competition.id))}
                  className='bg-white text-blue-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-colors hover-lift flex items-center gap-2'
                >
                  S&apos;inscrire maintenant <ArrowRight className='w-5 h-5' />
                </Link>
              ) : null}
              {hasResults && (
                <Link
                  to={resultsUrl}
                  className='border-2 border-amber-400/60 bg-amber-400/10 text-amber-200 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-amber-400/20 transition-colors flex items-center gap-2'
                >
                  <Trophy className='w-5 h-5' /> Voir les résultats
                </Link>
              )}
              <a
                href={'#shareButtons'}
                className='border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition-colors flex items-center gap-2'
              >
                <Share2 className='w-5 h-5' /> Partager
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
